/**
 * useDraggableScroll - 拖拽滚动 Hook
 *
 * 基于 Framer Motion 实现的水平拖拽滚动功能
 *
 * @module useDraggableScroll
 * @date 2026-01-29
 */

import { useRef, useState, useEffect } from 'react'

export interface UseDraggableScrollOptions {
  /**
   * 拖拽速度倍数
   * @default 1
   */
  dragSpeed?: number

  /**
   * 是否启用惯性滚动
   * @default true
   */
  enableMomentum?: boolean

  /**
   * 边界弹性系数（0-1，0表示无弹性）
   * @default 0.1
   */
  dragElastic?: number
}

export interface UseDraggableScrollReturn {
  /**
   * 容器 ref
   */
  containerRef: React.RefObject<HTMLDivElement>

  /**
   * 是否正在拖拽
   */
  isDragging: boolean

  /**
   * 滚动进度（0-100）
   */
  scrollProgress: number

  /**
   * 是否可以向左滚动
   */
  canScrollLeft: boolean

  /**
   * 是否可以向右滚动
   */
  canScrollRight: boolean

  /**
   * 滚动到指定位置
   */
  scrollTo: (position: number, smooth?: boolean) => void

  /**
   * 向左滚动
   */
  scrollLeft: (amount?: number) => void

  /**
   * 向右滚动
   */
  scrollRight: (amount?: number) => void
}

/**
 * 拖拽滚动 Hook
 *
 * @example
 * ```tsx
 * const { containerRef, isDragging, scrollProgress } = useDraggableScroll({
 *   dragSpeed: 1.5,
 *   enableMomentum: true
 * })
 *
 * return (
 *   <div ref={containerRef} className="overflow-x-auto">
 *     {items.map(item => <Item key={item.id} />)}
 *   </div>
 * )
 * ```
 */
export function useDraggableScroll(options: UseDraggableScrollOptions = {}): UseDraggableScrollReturn {
  const { dragSpeed = 1, enableMomentum = true, dragElastic = 0.1 } = options

  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // 更新滚动状态
  const updateScrollState = () => {
    const container = containerRef.current
    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container

    // 计算滚动进度
    const maxScroll = scrollWidth - clientWidth
    const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0
    setScrollProgress(Math.min(100, Math.max(0, progress)))

    // 更新滚动方向可用性
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < maxScroll - 1) // -1 容错
  }

  // 监听容器滚动事件
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // 初始化状态
    updateScrollState()

    // 监听滚动
    container.addEventListener('scroll', updateScrollState)

    // 监听窗口大小变化
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener('scroll', updateScrollState)
      resizeObserver.disconnect()
    }
  }, [])

  // 滚动到指定位置
  const scrollTo = (position: number, smooth = true) => {
    const container = containerRef.current
    if (!container) return

    container.scrollTo({
      left: position,
      behavior: smooth ? 'smooth' : 'auto',
    })
  }

  // 向左滚动
  const scrollLeftFn = (amount = 300) => {
    const container = containerRef.current
    if (!container) return

    const targetScroll = Math.max(0, container.scrollLeft - amount)
    scrollTo(targetScroll)
  }

  // 向右滚动
  const scrollRightFn = (amount = 300) => {
    const container = containerRef.current
    if (!container) return

    const maxScroll = container.scrollWidth - container.clientWidth
    const targetScroll = Math.min(maxScroll, container.scrollLeft + amount)
    scrollTo(targetScroll)
  }

  return {
    containerRef,
    isDragging,
    scrollProgress,
    canScrollLeft,
    canScrollRight,
    scrollTo,
    scrollLeft: scrollLeftFn,
    scrollRight: scrollRightFn,
  }
}
